<template>
  <div class="search-panel-container">
    <div class="search-panel">
      <LazySearchPanelLocations @update="setLocationObject" @searchHotel="setPropertyType" @searchActivity="setPropertyType" domain="hotel"/>
      <div class="vr"></div>
      <LazySearchPanelCalendar/>
      <div class="vr"></div>
      <LazySearchPanelVisitors />
      <button
          @click="search"
          :class="['btn btn-primary search-btn', {'tripening-search': $siteConfig.brand !== 'BookDirect'}]"
      >
        <span class="icon icon-search"></span>
        {{ t('Search')}}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSearchStore} from "~/store/search";
import { useRouter, useRoute } from "vue-router";

import dayjs from '~/utils/dayjs'
const _router = useRouter();
const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n();
const searchStore = useSearchStore();

const selectedLocation = ref<Object | null>(null);
let isRequestContinue = false;
const { $siteConfig } = useNuxtApp()
const searchPropertyType = ref('')

const ensureCityReady = async () => {
  const cityParam = Array.isArray(route.params.city) ? route.params.city[0] : route.params.city
  const citySlug = cityParam ? String(cityParam) : ''

  if (!citySlug) {
    return true
  }

  if (!searchStore.selectedCity || searchStore.selectedCity?.slug !== citySlug) {
    try {
      await searchStore.getCity(citySlug)
    } catch (error) {
      console.error('[SearchPanel] Failed to load city context', error)
      return false
    }
  }

  return true
}

const setPropertyType = (type:string) => {
  searchPropertyType.value = type
}

const setLocationObject = async (location: any) => {
  if (location.geo_place) {
    searchStore.selectedCity = location.geo_place
    searchStore.selectedLocation = location.geo_place
  } else if (location.isPoiSearch) {
    // POI/landmark selection - try to find the containing city
    searchStore.selectedLocation = location

    // Try to find the city in our database using search
    if (location.city_name) {
      try {
        const cityResults = await searchStore.getLocations({
          fts: location.city_name,
          domain: 'hotel',
          includePois: false,
        }) as any

        // Look for exact city match
        const matchedCity = cityResults?.cities?.find((c: any) =>
          c.name.toLowerCase() === location.city_name.toLowerCase() ||
          c.name.toLowerCase().includes(location.city_name.toLowerCase())
        )

        if (matchedCity) {
          searchStore.selectedCity = {
            ...matchedCity,
            // Store POI info for map centering
            poiName: location.name,
            poiLatitude: location.latitude,
            poiLongitude: location.longitude,
          }
          selectedLocation.value = location
          promptForDates()
          return
        }
      } catch (e) {
        console.warn('Could not find city for POI:', location.city_name)
      }
    }

    // Fallback: create a pseudo city with coordinates (will show error on search)
    searchStore.selectedCity = {
      name: location.city_name || location.name,
      slug: (location.city_name || location.name).toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      latitude: location.latitude,
      longitude: location.longitude,
      country_name: location.country_name,
      state_name: location.region,
      isPoiSearch: true,
      poiName: location.name,
      poiLatitude: location.latitude,
      poiLongitude: location.longitude,
    }
  } else {
    searchStore.selectedCity = location
    searchStore.selectedLocation = location
  }
  selectedLocation.value = location
  promptForDates()
}

const promptForDates = () => {
  if (!searchStore.formattedDates[0] || !searchStore.formattedDates[1]) {
    setTimeout(() => {
      document.getElementById('searchDates')?.click()
    }, 300)
  }
};


const moreIndicatorKey = computed(() => searchStore.hotelMoreIndicator)

onMounted(() => {
  window.addEventListener('scroll', debounce(onScroll, 200));
});

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

function search() {

  //validation
  if (!selectedLocation.value) {
    console.log('Please select a location')
    return;
  } else if (!searchStore.criteria.date_start || !searchStore.criteria.date_end) {
    console.log('Please select checkin and checkout dates')
    return;
  } else if (searchStore.getTotalVisitors < 1) {
    console.log('Please select number of visitors')
    return;
  } else if (searchStore.quantity < 1) {
    console.log('Please select number of rooms')
    return;
  }

  /*const params = {
    criteria: searchStore.criteria,
    filters: {
      geo_place_id: searchStore?.selectedCity?.id,
    }
  }*/
  const params = createDynamicParams(route)

  setQueryParams(params, searchPropertyType.value);
  //getDynamicHotels(moreIndicatorKey.value)
  //searchStore.searchHotels(params);
};

function createDynamicParams(_route: any) {
  const _query = _route.query.date_start && _route.query.date_end ? _route.query :  searchStore.criteria
  const _params = _route.params

  let params = {
    ..._query,
    citySlug: _params.city || searchStore?.selectedCity?.slug,
    adults: _query.adults,
    children: +_query?.children,
    ages: _query?.ages?.split?.(',').map(Number) || undefined,
  }

  if (params.date_start === params.date_end ) {
    params.date_end = dayjs(params.date_end).add(1, 'days').format('YYYY-MM-DD')
  }
  return params
}

async function getDynamicHotels(moreIndicator?: string) {
  const isRootCall = !moreIndicator

  if (isRootCall) {
    if (isRequestContinue) {
      return null
    }

    const cityReady = await ensureCityReady()
    if (!cityReady) {
      return null
    }

    isRequestContinue = true
    searchStore.clearHotelsResults()
  }

  const params = createDynamicParams(route)

  if (moreIndicator) {
    params.token = moreIndicator
  } else {
    delete params.token
  }

  try {
    const res = await searchStore.searchHotels(params)

    if (res?.more) {
      searchStore.hotelMoreIndicator = res.more
      await getDynamicHotels(res.more)
    } else if (res?.next) {
      searchStore.hotelMoreIndicator = res.next
    } else {
      searchStore.hotelMoreIndicator = null
    }

    return res
  } catch (err) {
    console.log(err)
    return null
  } finally {
    if (isRootCall) {
      isRequestContinue = false
    }
  }
}

async function refreshHotels() {
  await getDynamicHotels()
}

function normalizeQuery(query: Record<string, any> | undefined) {
  if (!query) {
    return {}
  }

  const normalized: Record<string, any> = {}
  Object.keys(query)
      .filter((key) => key !== 'map')
      .sort()
      .forEach((key) => {
        const value = query[key]
        if (Array.isArray(value) && value.length === 1) {
          normalized[key] = value[0]
        } else {
          normalized[key] = value
        }
      })

  return normalized
}

function onScroll() {

  let windowTop = (window.top as Window)?.scrollY || 0
  const windowHeight = window.innerHeight
  const height = document.querySelector('body')?.offsetHeight || 0
  const footerHeight = document.querySelector('footer')?.offsetHeight || 0

  if (height - windowHeight - footerHeight - 50  < windowTop) {

    if ((searchStore.hotelProductsFirstPage?.length || searchStore.hotelsProducts?.length) && searchStore.hotelMoreIndicator && !searchStore.loadingMoreHotelsProducts) {

      getDynamicHotels(moreIndicatorKey.value || undefined)
    }
  }
}

function createSlug(id:number, name: string, slug = null) {
  let result = ''
  if (slug) {
    result = `${id}-${slug}`
  } else {
    const url = name?.replace(/ /g, '-')
        .split(',').join('')
        .split('/').join('-')
        .split(':').join('-')
        .split('(').join('')
        .split(')').join('')
        .split('---').join('-')
        .split('%').join('-')
        .split('&').join('-')
        .toLowerCase() || ''

    result = `${id}-${url}`
  }

  return result
}
function setQueryParams(params: any, propertyType: string) {
  let routeSlug = ''
  const query: Record<string, any> = {..._router.currentRoute.value.query, ...searchStore.criteria}
  if (query.ages) {
    query.ages = query.ages.join(',')
  }

  // Get route slug from the resolved selectedCity (which has the real city data for POIs)
  if ((selectedLocation.value as any)?.geo_place) {
    routeSlug = (selectedLocation.value as any)?.geo_place?.slug
  } else if (searchStore.selectedCity?.slug) {
    // Use selectedCity slug (works for both regular cities and POI-resolved cities)
    routeSlug = searchStore.selectedCity.slug
  } else {
    routeSlug = (selectedLocation.value as any)?.slug
  }

  // Add POI coordinates to query for map centering (if POI was selected)
  if (searchStore.selectedCity?.poiLatitude && searchStore.selectedCity?.poiLongitude) {
    query.poi_lat = searchStore.selectedCity.poiLatitude
    query.poi_lon = searchStore.selectedCity.poiLongitude
    query.poi_name = searchStore.selectedCity.poiName
  }

  // Ensure we have a valid slug
  if (!routeSlug) {
    console.error('No valid route slug for location:', selectedLocation.value)
    return
  }

  if (propertyType) {
    routeSlug = `${routeSlug}/${createSlug((selectedLocation.value as any)?.id, routeSlug)}`
  }

  _router.push(localePath({path: `/${propertyType || 'hotels'}/${routeSlug}`, query: {...query, quantity: searchStore.quantity}}))
}

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
      const normalizedNew = normalizeQuery(newQuery)
      const normalizedOld = normalizeQuery(oldQuery)

      if (!normalizedNew.date_start || !normalizedNew.date_end) {
        return
      }

      const hasPrevious = Object.keys(normalizedOld).length > 0
      if (hasPrevious && JSON.stringify(normalizedNew) === JSON.stringify(normalizedOld)) {
        return
      }

      await refreshHotels()
    },
    { deep: true }
);
</script>

<style lang="scss">
$blackColorFilter: brightness(0) saturate(100%) invert(18%) sepia(0%) saturate(1189%) hue-rotate(154deg) brightness(92%) contrast(101%); //#292929

// Override icon filter in dark mode — use color instead of filter
:root[data-theme="dark"] .search-input__icon {
  filter: brightness(0) saturate(100%) invert(90%) sepia(0%) saturate(0%) brightness(90%) contrast(100%);
}

.search-input {
  display: flex;
  gap: 8px;
  align-items: center;
  background: var(--white);
  position: relative;
  padding: 8px 16px;
  height: 56px;
  border-radius: 6px;
  border: 2px solid transparent;

  &__container {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: flex-start;
  }

  //tablet
  @media (max-width: 1024px) {
    padding: 8px 4px;
  }

  &:hover, &:focus {
    background-color: var(--light-grey);
    .search-input__input--placeholder {
      color: var(--black);
      font-weight: 500;
    }
  }

  &.in-focus, &.open {
    background-color: var(--white) !important;
    border-color: var(--blue);
  }

  &__icon {
    filter: $blackColorFilter;
    color: var(--black);
    font-size: 24px;
  }

  &__input {
    border: none;
    outline: none;
    width: 100%;
    text-align: left;
    background: transparent;
    color: var(--black);

    &--placeholder {
      color: var(--dark-grey);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__label {
    position: absolute;
    top: 8px;
    left: 48px;
    color: var(--dark-grey);
    font-size: 12px;
    font-weight: 400;
    line-height: 133.333%;

    //tablet
    @media (max-width: 1024px) {
      left: 36px;
    }

    .no-icon & {
      left: 16px;

      @media screen and (max-width: 768px) {
        left: 4px;
      }
    }
  }

  &__body {
    margin-top: 16px;
    display: flex;
    gap: 8px;
    width: 100%;
  }
}
</style>

<style scoped lang="scss">
.search-panel-container {
  background: var(--light-grey);
  border-bottom: 1px solid var(--grey-border);
  width: 100%;
  padding: 20px 0;
  position: relative;
  z-index: 100;

  :root[data-theme="dark"] & {
    background: var(--light-grey);
  }

  .search-panel {
    background: var(--white);
    border: 1px solid var(--grey-border);
    padding: 12px;
    display: flex;
    align-items: center;
    gap: 4px;
    border-radius: 10px;
    width: 100%;
    max-width: 1120px;
    margin: 0 auto;


    //tablet
    @media (max-width: 1024px) {
      flex-direction: column;
    }

    & > div {
      //tablet
      @media (max-width: 1024px) {
        width: 100%;
      }
    }

    :global(.locations) {
      flex: 1;
    }

    .vr {
      background-color: var(--grey-border);
      width: 1px;
      height: 56px;
      @media (max-width: 1024px) {
        width: 100%;
        height: 1px;
      }
    }
  }
  @media (max-width: 1024px) {
    padding: 20px 16px;
  }
}

.search-btn {
  width: 135px;
  height: 56px;
  font-size: 20px;
  font-weight: 600;
  line-height: 120%;

  &.tripening-search {
    border-radius: 4px;
    background-color: var(--blue);
  }
  //tablet
  @media (max-width: 1024px) {
    max-width: 100%;
    width: 100%;
    margin-top: 8px;
  }

  .icon {
    font-size: 24px;
    margin-right: 2px;
  }
}
</style>
